<template>
    <div class="pt-3">
        <div v-if="loadError" class="alert alert-danger" role="alert">
            {{ loadError }}
            <button type="button" class="btn btn-link p-0" @click="fetchCatalogue">Try again</button>
        </div>
        <div v-else-if="!catalogue" class="text-muted">Loading delivery options…</div>
        <div v-else class="row">
            <div v-for="side in sides" :key="side" class="col-md-6 mb-3">
                <h3 class="h5">{{ side === 'pickup' ? 'Pickup' : 'Dropoff' }}</h3>
                <div v-if="!catalogue[side].editable" class="alert alert-secondary">
                    <span v-if="catalogue[side].cutOffPassed">Locked: past cut-off.</span>
                    <span v-else>Locked: booking status does not allow changes.</span>
                </div>
                <fieldset v-else :disabled="applying[side]">
                    <div class="form-group">
                        <label :for="`post-confirm-${side}-date`">{{ side === 'pickup' ? 'New pickup date' : 'New delivery date' }}</label>
                        <date-picker
                            :id="`post-confirm-${side}-date`"
                            v-model="forms[side].newDate"
                            value-type="format"
                            format="YYYY-MM-DD"
                            :lang="datePickerOptions.lang"
                            :disabled-date="date => disabledDate(date, side)"
                            :disabled="applying[side] || (side === 'dropoff' && !catalogue.dropoff.dropoffReady)"
                            :clearable="true"
                            input-class="form-control"
                        ></date-picker>
                        <small v-if="dateFee(side) !== null" class="form-text text-muted">
                            Date change fee: {{ dateFee(side).toFixed(2) }} kr. excl. VAT
                        </small>
                        <small v-if="side === 'dropoff' && !catalogue.dropoff.dropoffReady" class="form-text text-muted">
                            A new delivery date can only be applied once the goods are ready for dropoff.
                        </small>
                    </div>
                    <div class="form-group" v-if="catalogue[side].shorterTimeIntervals.length">
                        <label :for="`post-confirm-${side}-interval`">Shorter time interval</label>
                        <select :id="`post-confirm-${side}-interval`" class="form-control" v-model="forms[side].intervalIndex">
                            <option :value="null">Keep current ({{ currentIntervalLabel(side) }})</option>
                            <option v-for="(option, index) in catalogue[side].shorterTimeIntervals" :key="index" :value="index">
                                {{ option.from }} - {{ option.to }}
                                {{ option.basePrice ? `(+${option.basePrice.toFixed(2)} kr. excl. VAT)` : '(free)' }}
                            </option>
                        </select>
                    </div>

                    <h4 class="h6 mt-4">{{ side === 'pickup' ? 'Pickup address' : 'Delivery address' }}</h4>
                    <div v-for="field in ['name', 'street']" :key="field" class="form-group">
                        <label :for="`post-confirm-${side}-${field}`">{{ field === 'name' ? 'Name' : 'Address' }}</label>
                        <input :id="`post-confirm-${side}-${field}`" class="form-control" v-model="forms[side].address[field]" maxlength="255">
                    </div>
                    <div class="form-row">
                        <div class="form-group col-4">
                            <label :for="`post-confirm-${side}-zip`">Zip</label>
                            <input :id="`post-confirm-${side}-zip`" class="form-control" v-model="forms[side].address.zip" inputmode="numeric">
                        </div>
                        <div class="form-group col-8">
                            <label :for="`post-confirm-${side}-city`">City</label>
                            <input :id="`post-confirm-${side}-city`" class="form-control" v-model="forms[side].address.city" maxlength="255">
                        </div>
                    </div>
                    <div class="form-group">
                        <label :for="`post-confirm-${side}-country`">Country</label>
                        <select :id="`post-confirm-${side}-country`" class="form-control" v-model="forms[side].address.country_id">
                            <option v-for="country in countryList" :key="country.id" :value="country.id">{{ country.name }}</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label :for="`post-confirm-${side}-phone`">Phone</label>
                        <input :id="`post-confirm-${side}-phone`" type="tel" class="form-control" v-model="forms[side].address.phone" maxlength="255">
                    </div>
                    <div class="form-group">
                        <label :for="`post-confirm-${side}-email`">Email</label>
                        <input :id="`post-confirm-${side}-email`" type="email" class="form-control" v-model="forms[side].email" maxlength="255">
                    </div>
                    <small v-if="zipChanged(side)" class="form-text text-muted mb-3">
                        Changing the zip code may move the {{ side === 'pickup' ? 'pickup' : 'delivery' }} date to a later day.
                    </small>

                    <div class="form-group" v-if="side === 'dropoff' && catalogue.dropoff.permits.length">
                        <label for="post-confirm-permit">Fuldmagt</label>
                        <select id="post-confirm-permit" class="form-control" v-model="forms.dropoff.permit">
                            <option :value="null">Keep current ({{ catalogue.dropoff.currentPermit || 'No permission given' }})</option>
                            <option v-for="permit in catalogue.dropoff.permits" :key="permit" :value="permit">{{ permit }}</option>
                        </select>
                        <small class="form-text text-muted">Permission to leave the delivery at the selected location.</small>
                    </div>

                    <div class="form-group" v-if="catalogue[side].callFromDriver">
                        <div class="form-check">
                            <input :id="`post-confirm-${side}-driver-call`" class="form-check-input" type="checkbox"
                                   v-model="forms[side].callFromDriver" :disabled="catalogue[side].callFromDriver.selected">
                            <label :for="`post-confirm-${side}-driver-call`" class="form-check-label"
                                   :class="{'text-muted': catalogue[side].callFromDriver.selected}">
                                {{ catalogue[side].callFromDriver.name }}
                                <span v-if="catalogue[side].callFromDriver.selected">(added)</span>
                                <span v-else>(+{{ catalogue[side].callFromDriver.basePrice.toFixed(2) }} kr. excl. VAT)</span>
                            </label>
                        </div>
                    </div>

                    <div class="form-group" v-if="catalogue[side].selectedServices.length || catalogue[side].primeServices.length">
                        <h4 class="h6 mt-4">Services</h4>
                        <div v-for="service in catalogue[side].selectedServices" :key="`selected-${service.id}`" class="form-check">
                            <input :id="`post-confirm-${side}-selected-${service.id}`" class="form-check-input" type="checkbox" checked disabled>
                            <label :for="`post-confirm-${side}-selected-${service.id}`" class="form-check-label text-muted">{{ service.name }} (added)</label>
                        </div>
                        <div v-for="service in catalogue[side].primeServices" :key="service.id" class="form-check">
                            <input :id="`post-confirm-${side}-service-${service.id}`" class="form-check-input" type="checkbox" :value="service.id" v-model="forms[side].primeServiceIds">
                            <label :for="`post-confirm-${side}-service-${service.id}`" class="form-check-label">
                                {{ service.name }} (+{{ service.price.toFixed(2) }} kr. excl. VAT)
                            </label>
                        </div>
                        <small v-if="side === 'dropoff' && forms[side].primeServiceIds.length" class="form-text text-muted">
                            Adding services may move the delivery date to a later day.
                        </small>
                    </div>
                    <div class="form-group" v-if="side === 'dropoff' && catalogue.dropoff.addOns.length">
                        <h4 class="h6 mt-4">Add-ons</h4>
                        <div v-for="addOn in catalogue.dropoff.addOns" :key="addOn.id" class="form-check">
                            <input :id="`post-confirm-add-on-${addOn.id}`" class="form-check-input" type="checkbox" :value="addOn.id" v-model="forms.dropoff.addOnIds">
                            <label :for="`post-confirm-add-on-${addOn.id}`" class="form-check-label">
                                {{ addOn.name }} (+{{ addOn.basePrice.toFixed(2) }} kr. excl. VAT)
                            </label>
                        </div>
                    </div>
                    <div v-if="messages[side].error" class="alert alert-danger" role="alert">{{ messages[side].error }}</div>
                    <div v-if="messages[side].success" class="alert alert-success" role="status">
                        Changes applied.
                        <span v-if="messages[side].autoPushedTo">The delivery date was moved to {{ messages[side].autoPushedTo }}.</span>
                        Reloading…
                    </div>
                    <button type="button" class="btn btn-primary" :disabled="applying[side] || !hasChanges(side)" @click="applyChanges(side)">
                        {{ applying[side] ? 'Applying…' : `Apply ${side} changes` }}
                    </button>
                </fieldset>
            </div>
        </div>
    </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import datePickerOptionsMixin from '../../mixins/datePickerOptionsMixin';

export default {
    name: 'PostConfirmEditPanel',
    components: {DatePicker},
    mixins: [datePickerOptionsMixin],
    props: {
        optionsUrl: {type: String, required: true},
        applyDropoffUrl: {type: String, required: true},
        applyPickupUrl: {type: String, required: true},
        countryList: {type: [Array, Object], required: true},
    },
    data() {
        return {
            catalogue: null,
            loadError: null,
            sides: ['pickup', 'dropoff'],
            applying: {pickup: false, dropoff: false},
            forms: {pickup: null, dropoff: null},
            messages: {
                pickup: {success: false, error: null, autoPushedTo: null},
                dropoff: {success: false, error: null, autoPushedTo: null},
            },
        };
    },
    mounted() {
        this.fetchCatalogue();
    },
    methods: {
        fetchCatalogue() {
            this.loadError = null;
            axios.get(this.optionsUrl)
                .then(({data}) => {
                    for (const side of this.sides) {
                        this.forms[side] = {
                            newDate: null,
                            intervalIndex: null,
                            primeServiceIds: [],
                            addOnIds: [],
                            callFromDriver: data.data[side].callFromDriver?.selected || false,
                            permit: null,
                            address: {...data.data[side].address},
                            email: data.data[side].email || '',
                        };
                    }
                    this.catalogue = data.data;
                })
                .catch(() => {
                    this.loadError = 'Could not load delivery options.';
                });
        },
        dateFee(side) {
            const date = this.forms[side].newDate;
            const price = this.catalogue[side].datePrices[date];
            return !date || price === undefined ? null : price;
        },
        currentIntervalLabel(side) {
            const interval = this.catalogue[side].currentInterval;
            return interval.from && interval.to ? `${interval.from} - ${interval.to}` : '—';
        },
        zipChanged(side) {
            return this.forms[side].address.zip !== this.catalogue[side].address.zip;
        },
        addressChanged(side) {
            return ['name', 'street', 'city', 'zip', 'country_id', 'phone'].some(field =>
                String(this.forms[side].address[field] || '') !== String(this.catalogue[side].address[field] || '')
            );
        },
        emailChanged(side) {
            return (this.forms[side].email || '') !== (this.catalogue[side].email || '');
        },
        permitChanged(side) {
            return side === 'dropoff' && this.forms.dropoff.permit !== null &&
                this.forms.dropoff.permit !== this.catalogue.dropoff.currentPermit;
        },
        hasNewDriverCall(side) {
            return this.forms[side].callFromDriver && !this.catalogue[side].callFromDriver.selected;
        },
        hasChanges(side) {
            const form = this.forms[side];
            return !!form.newDate || form.intervalIndex !== null || form.primeServiceIds.length > 0 ||
                form.addOnIds.length > 0 || this.hasNewDriverCall(side) || this.addressChanged(side) || this.emailChanged(side) || this.permitChanged(side);
        },
        disabledDate(date, side) {
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            const routeDays = this.catalogue[side].routeAvailableDays;
            if (Object.keys(routeDays).length && !Object.keys(routeDays).includes(date.getDay().toString())) {
                return true;
            }
            return this.catalogue[side].datePrices[key] === undefined;
        },
        applyChanges(side) {
            const form = this.forms[side];
            const payload = {};
            if (form.newDate) {
                payload[side === 'pickup' ? 'newPickupDate' : 'newDeliveryDate'] = form.newDate;
            }
            if (form.intervalIndex !== null) {
                const option = this.catalogue[side].shorterTimeIntervals[form.intervalIndex];
                payload.interval = {from: option.from, to: option.to};
            }
            if (form.primeServiceIds.length) {
                payload.primeServiceIds = form.primeServiceIds;
            }
            if (form.addOnIds.length) {
                payload.addOnIds = form.addOnIds;
            }
            if (this.hasNewDriverCall(side)) {
                payload.callFromDriver = true;
            }
            if (this.addressChanged(side)) {
                payload.address = {...form.address};
            }
            if (this.emailChanged(side)) {
                payload.email = form.email || null;
            }
            if (this.permitChanged(side)) {
                payload.permit = form.permit;
            }

            this.applying[side] = true;
            this.messages[side] = {success: false, error: null, autoPushedTo: null};
            const url = side === 'pickup' ? this.applyPickupUrl : this.applyDropoffUrl;
            axios.post(url, payload)
                .then(({data}) => {
                    this.messages[side].success = true;
                    this.messages[side].autoPushedTo = data.autoPushedTo;
                    setTimeout(() => window.location.reload(), data.autoPushedTo ? 4000 : 1500);
                })
                .catch(error => {
                    this.applying[side] = false;
                    const response = error.response?.data;
                    this.messages[side].error = response?.errors ?
                        Object.values(response.errors).flat().join(' ') :
                        response?.message || 'Could not apply the changes.';
                });
        },
    },
};
</script>
